import { html, useEffect, useState } from '/vendor/htm-preact.module.js';
import { del, get, post, put } from '/api.js';
import { Icon } from '/icons.js';
import { AddToCircle } from '/members.js';
import { Discover, Requests } from '/discover.js';
import { myCircles, scoped } from '/circlechoice.js';
import { useRead } from '/read.js';
import { Chat } from '/chat.js';
import { RoleMarks } from '/rolemark.js';
import { RowSkeleton } from '/skeleton.js';
import { ImageUpload, Picture } from '/imageupload.js';
import { TeamInLeague } from '/team.js';

/**
 * The Circle tab — the mockup's TEAM, backed by what Cueforge actually has.
 *
 * The mockup's team card carries a division, a league position and a fixture
 * list. None of those exist here, so the card carries what does: whose circle,
 * how many are in it, and where you stand in it. The roster keeps the form
 * dots, which are real — they come from the ledger.
 */

/**
 * Who may manage this circle.
 *
 * On the circle, not in the admin panel — deliberately. Putting circle grants
 * in the panel would force can_admin to count circle grants again, and every
 * person who had ever created a circle would be shown an "Admin" row leading
 * somewhere that misdescribes them (finding F8). Here there is no such
 * pressure, and this is where you are already looking at the circle.
 *
 * Rendered only when you hold circle.grant.write here, which the creator does
 * through the wildcard they get on creation.
 */
function CircleManagers({ circleId, listed, isTeam, accounts, holds,
                          page, setPage, onChanged, onDeleted }) {
  const [caps, setCaps] = useState(null);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    get('/api/me/capabilities').then(setCaps).catch(() => setCaps(null));
  }, [circleId]);

  if (!caps) return null;
  const mayGrant = caps.is_admin || caps.grants.some(
    (g) => g.scope_type === 'circle' && g.scope_id === circleId
           && (g.capability === '*' || g.capability === 'circle.grant.write'));
  if (!mayGrant) return null;


  // Two roles, named for what they DO rather than for what the bundle is
  // called. "Roster manager" is `league.team.select` — it picks the team
  // sheet and cannot add or remove a single person, because that is
  // `circle.member.write`, which only the circle-scoped `*` carries. A
  // control labelled with the bundle name would promise the opposite of
  // what it grants, which is exactly the confusion this pair replaces.
  const ROLES = {
    owner: { capability: '*', label: 'Runs the team',
             sub: 'Adds and removes people, and everything else here' },
    sheet: { capability: 'league.team.select', label: 'Picks the team',
             sub: 'Chooses who plays each fixture, and nothing else' },
  };

  async function setRole(userId, role) {
    setError('');
    const wanted = ROLES[role].capability;
    try {
      // Revoke the other one first. Granting both would leave somebody
      // holding a set that matches no bundle, which `bundles.match` reports
      // as CUSTOM — accurate, and useless as a label on a screen offering
      // exactly two roles.
      const other = role === 'owner' ? ROLES.sheet : ROLES.owner;
      await post('/api/grants/revoke', {
        subject_id: userId, scope_type: 'circle', scope_id: circleId,
        capability: other.capability,
      }).catch(() => {});
      await post('/api/grants', {
        subject_id: userId, scope_type: 'circle', scope_id: circleId,
        capability: wanted, cascade: false,
      });
      await onChanged();
    } catch (e) { setError(e.detail || e.message); }
  }

  async function clearRole(userId, capability) {
    setError('');
    try {
      await post('/api/grants/revoke', {
        subject_id: userId, scope_type: 'circle', scope_id: circleId,
        capability,
      });
      await onChanged();
    } catch (e) { setError(e.detail || e.message); }
  }

  // Admin only — an Owner's circle-scoped `*` gets them into this panel at
  // all (mayGrant, above) but not this. The server enforces the same line
  // (Service.delete_circle uses _require_admin, not P.ALL), so this is a
  // convenience, not the security boundary.
  async function deleteCircle() {
    setError('');
    setDeleting(true);
    try {
      await del(`/api/circles/${encodeURIComponent(circleId)}`);
      onDeleted();
    } catch (e) {
      setError(e.detail || e.message);
      setDeleting(false);
      setConfirming(false);
    }
  }

  return html`
    <div class="stack" data-testid="circle-managers">
      ${/* A team is found through its league, not by listing itself — the
            API refuses the toggle outright (Task 1), so the screen does not
            offer it either. */''}
      ${!isTeam && html`
        <label>Finding this circle</label>
        <div class="rowbetween">
          <span class="sub">
            ${listed
              ? 'Listed — people can find it and ask to join'
              : 'Not listed — only people you add are in it'}
          </span>
          <button class="secondary" data-testid="circle-listed-toggle"
                  onClick=${async () => {
                    setError('');
                    try {
                      await put(`/api/circles/${encodeURIComponent(circleId)}/listing`,
                                { listed: !listed });
                      // No local copy of `listed`: onChanged refetches
                      // /api/me/circles, which carries it, so the prop is the
                      // single answer. Mirroring it into state meant the value
                      // was captured once per mount and only stayed correct
                      // because switching circles happens to unmount this.
                      await onChanged();
                    } catch (e) { setError(e.detail || e.message); }
                  }}>${listed ? 'Stop listing' : 'List it'}</button>
        </div>`}

      ${/* A SUB-PAGE, not another block on a long screen — issue #56. Roles
            are a sitting's work rather than a glance, and stacked under the
            listing toggle they were something you scrolled past on the way to
            the delete button.

            ONLY the roles fold away. Listing this circle and deleting it stay
            on Manage where they were: the first cut hid all three behind the
            row, because they share this component, and moving two things
            nobody asked to move is how a fix becomes a regression. */''}
      ${!page && html`
        <button class="secondary card rowbetween" data-testid="circle-go-roles"
                onClick=${() => setPage('roles')}>
          <span>Who can manage this circle</span>
          <span class="sub">Who runs it, who picks the team</span>
        </button>`}

      ${page && html`
      <div class="stack" data-testid="circle-roles">
      <div class="rowbetween">
        <label>Who can manage this circle</label>
        <button class="secondary" style="width:auto"
                data-testid="circle-roles-back"
                onClick=${() => setPage(null)}>Manage</button>
      </div>
      ${error && html`<p class="sub" data-testid="circle-managers-error"
                         style="color:var(--danger)">${error}</p>`}
      ${accounts.length === 0 && html`
        <p class="empty" data-testid="circle-no-managers">
          Nobody else in this circle to hand it to yet.
        </p>`}
      ${accounts.map((p) => {
        // What they hold HERE, so the row says so rather than offering
        // "Make owner" to somebody who already is one and appearing to do
        // nothing when pressed.
        const has = (holds || {})[p.user_id];
        return html`
        <div key=${p.user_id} class="card stack"
             data-testid=${`circle-manager-${p.user_id}`}>
          <div class="rowbetween">
            <span><${RoleMarks} roles=${p.roles} only=${1} />${p.name}</span>
            <span class="sub" data-testid=${`circle-role-${p.user_id}`}>
              ${has || 'No role here'}
            </span>
          </div>
          <div class="row pair">
            <button class="secondary" data-testid="make-owner"
                    onClick=${() => setRole(p.user_id, 'owner')}>
              ${ROLES.owner.label}
            </button>
            <button class="secondary" data-testid="make-sheet-picker"
                    onClick=${() => setRole(p.user_id, 'sheet')}>
              ${ROLES.sheet.label}
            </button>
          </div>
          ${has && html`
            <button class="secondary" data-testid="clear-role"
                    onClick=${() => clearRole(p.user_id,
                      has === 'Owner' ? ROLES.owner.capability
                                      : ROLES.sheet.capability)}>
              Take it away
            </button>`}
        </div>`;
      })}
      </div>`}

      ${/* Admin only, and only for a plain circle. A team is a circle
            entered into a league (Service.leagues_fielding), and its league
            depends on it existing — refused at the API with a clear message,
            and not offered here at all rather than offered and refused. */''}
      ${caps.is_admin && html`
        <label>Delete this circle</label>
        ${isTeam
          ? html`
            <p class="sub" data-testid="circle-delete-team-note">
              This is a team in a league — withdraw it from the league before
              it can be deleted.
            </p>`
          : html`
            <div class="stack" data-testid="circle-delete">
              <p class="sub">
                Takes it off every list. Matches, ratings and chat already
                recorded here are kept, not undone.
              </p>
              ${confirming
                ? html`
                  <div class="row pair">
                    <button class="secondary" data-testid="circle-delete-cancel"
                            disabled=${deleting}
                            onClick=${() => setConfirming(false)}>Cancel</button>
                    <button data-testid="circle-delete-confirm"
                            disabled=${deleting}
                            style="color:var(--danger);border-color:var(--danger)"
                            onClick=${deleteCircle}>
                      ${deleting ? 'Deleting…' : 'Yes, delete it'}
                    </button>
                  </div>`
                : html`
                  <button class="secondary" data-testid="circle-delete-start"
                          style="color:var(--danger)"
                          onClick=${() => setConfirming(true)}>
                    Delete circle
                  </button>`}
            </div>`}
      `}
    </div>`;
}

/**
 * The Circles tab: the circles you are in.
 *
 * It used to render ONE circle with a <select> to swap between them, so being
 * in four circles was a dropdown rather than four things you have. A list, and
 * then a page each — which is also what gives a circle a URL.
 */
export function CircleTab({ authState, onGo }) {
  const [mine, error] = useRead('/api/me/circles');
  const [round, setRound] = useState(0);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState('');
  const [search, setSearch] = useState('');

  const again = async () => setRound((r) => r + 1);

  // Making a circle lives HERE, and only here. It used to be on the form for
  // starting a match, where it was a decision about the rest of your life
  // interrupting a decision about the next twenty minutes. This is the screen
  // you are on when you are thinking about circles.
  const create = async () => {
    setBusy(true); setFailed('');
    try {
      const made = await post('/api/circles', { name: name.trim() || 'Matches' });
      await post(`/api/circles/${made.circle_id}/members`,
                 { user_id: authState.user_id });
      setName('');
      await again();
    } catch (e) {
      setFailed(e.detail || e.message);
    } finally { setBusy(false); }
  };

  const makeOne = html`
    <div class="stack" data-testid="circle-make">
      <label>Start a circle</label>
      <div class="row pair">
        <input data-testid="circle-new-name" placeholder="Thursday crew"
               value=${name} onInput=${(e) => setName(e.target.value)} />
        <button data-testid="circle-create" disabled=${busy}
                onClick=${create}>Create</button>
      </div>
      ${failed && html`<p class="error" data-testid="circle-make-error">
                         ${failed}
                       </p>`}
    </div>`;

  // Same rule as the stats screens: a failed refresh is not something to
  // paper over with the last answer.
  if (error) {
    return html`<p class="error" data-testid="circle-error">
                  ${error.detail || error.message}
                </p>`;
  }

  // Nothing at all is known yet — the one moment there is genuinely nothing
  // to draw. Anything else here would be a claim about circles nobody has
  // asked about: this screen used to say "You are not in a circle yet" before
  // it had asked, which is the confident, specific, wrong answer the
  // leaderboard already carries a comment about refusing to give.
  if (mine === undefined) {
    return html`<div class="stack" data-testid="circle-waiting">
                  <${RowSkeleton} /><${RowSkeleton} />
                </div>`;
  }

  const circles = mine.circles;

  if (circles.length === 0) {
    // Discovery belongs HERE most of all: somebody in no circles is exactly
    // who is looking for one. It was first written only into the per-circle
    // view, where the person with none never saw it.
    return html`
      <p class="sub" data-testid="circle-none">
        You are not in a circle yet. A circle is who you play — record a match
        and that makes your first one, or ask to join one below.
      </p>
      ${makeOne}
      <${Requests} kind="circle" onChanged=${again} />
      <${Discover} kind="circle" onChanged=${again} />`;
  }

  // GitHub issue #64. No debounce and no request, the same as the people
  // search issue #43 already ships: the filter runs over a list that is
  // already here, so the work per keystroke is one pass over a handful of
  // short strings.
  //
  // The VENUE is matched as well as the name, because "which of these am I
  // looking for" is answered by the pub at least as often as by the name —
  // that is most of why somebody is in two of them (see `venue_name`'s own
  // comment on the server side).
  const query = search.trim().toLowerCase();
  const shown = query
    ? circles.filter((c) => `${c.name} ${c.venue_name || ''}`
        .toLowerCase().includes(query))
    : circles;

  return html`
    ${makeOne}
    <div class="stack" data-testid="circle-list">
      <label>Your circles</label>
      ${/* Drawn whatever the length of the list, matching issue #43. A
            threshold would be a number nobody can see, and it would move the
            controls under somebody's thumb on the day they joined their
            sixth circle. */''}
      <input data-testid="circle-search" value=${search}
             placeholder="Search your circles"
             onInput=${(e) => setSearch(e.target.value)} />

      ${query && shown.length === 0 && html`
        ${/* Rather than an empty box, which reads as "you are in no circles"
              — the exact words this screen uses when that is true. */''}
        <p class="sub" data-testid="circle-search-none">
          ${`No circle matches “${search.trim()}”.`}
        </p>`}

      ${shown.map((c) => html`
        <button key=${c.id} class="secondary card circlerow"
                data-testid=${`circle-item-${c.id}`}
                onClick=${() => onGo('circle', c.id)}>
          <span style="display:flex;align-items:center;gap:.5rem">
            ${/* Drawn only when there is one, so the grid's three columns and
                  every row's height are unchanged for the circles that have
                  none -- which is all of them today. */''}
            <${Picture} imageId=${c.badge} className="avatar" alt="" />
            ${c.name}
          </span>
          ${/* Issue #53: the venue is its own column, not a second line under
                the name. Sharing that box meant a long team name and its pub
                competed for the same width and the name clipped.

                RENDERED EVEN WHEN EMPTY. A circle with no venue is ordinary —
                that is what a friendly is — and dropping the element would
                leave that row with two grid children instead of three, so
                everything after it would slide left and the column would
                break exactly where it is least expected. */''}
          <span class="sub" data-testid=${`circle-at-${c.id}`}
                style="text-transform:none">${c.venue_name || ''}</span>
          <span style="text-align:right">
            ${/* One list holds both kinds, so each row says which it is
                  rather than the list picking one word for everything. */''}
            <span class="sub mono" style="display:block"
                  data-testid=${`circle-kind-${c.id}`}>
              ${c.is_team ? 'Team' : 'Circle'}
            </span>
            <span class="sub mono" data-testid=${`circle-count-${c.id}`}>
              ${`${c.players} ${c.players === 1 ? 'player' : 'players'}`}
            </span>
          </span>
        </button>`)}
    </div>
    <${Requests} kind="circle" onChanged=${again} />
    <${Discover} kind="circle" onChanged=${again} />
    <button class="secondary" data-testid="circle-h2h"
            onClick=${() => onGo('h2h')}>Head to head in full</button>`;
}


/** One circle: who is in it, how you are doing, and who runs it. */
export function CircleScreen({ authState, circleId, view, onGo, onDone }) {
  const [circles, setCircles] = useState([]);
  const [rows, setRows] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [h2h, setH2h] = useState([]);
  const [error, setError] = useState('');
  const [claimError, setClaimError] = useState('');
  // The team's own row in its league: undefined while loading, null when
  // this circle is not matched to a side (a mirrored league's team is
  // linked by hand, and until somebody does there is nothing to show).
  const [teamInLeague, setTeamInLeague] = useState(undefined);
  const [teamLeagueId, setTeamLeagueId] = useState(null);
  // Which Manage sub-page, held here rather than in the URL — the same call
  // the committee's sub-pages make (leagues.js): the rooms above ARE in the
  // URL, but a sub-page of one of them is not a place you link somebody to.
  const [managePage, setManagePage] = useState(null);
  // Pubs this circle could ask. Only for the manage room, so fetched with
  // the rest rather than on its own round trip.
  const [venues, setVenues] = useState([]);
  const [round, setRound] = useState(0);

  useEffect(() => {
    let ignore = false;
    setRows(null);
    Promise.all([myCircles(), get('/api/people'),
                 get(`/api/players/me/head-to-head${scoped(circleId)}`),
                 get('/api/venues').catch(() => [])])
      .then(([mine, all, records, pubs]) => {
        if (ignore) return;
        setCircles(mine.circles);
        setAccounts(all);
        setVenues(pubs || []);
        setH2h(records);
        const circle = mine.circles.find((c) => c.id === circleId) || {};
        if (circle.is_team) {
          // A team has no friendlies leaderboard to ask for -- the API
          // refuses it if asked (Task 2). Its roster for managing
          // membership comes from who is in it, not from a ranking that
          // no longer applies.
          const me = all.find((a) => a.user_id === authState.user_id);
          setRows([
            { player_id: authState.user_id, name: (me && me.name) || '' },
            ...(circle.people || []).map((p) => (
              { player_id: p.user_id, name: p.name })),
          ]);
          return undefined;
        }
        return get(`/api/circles/${circleId}/leaderboard`)
          .then((r) => { if (!ignore) setRows(r); });
      })
      .catch((e) => { if (!ignore) setError(e.detail || e.message); });
    return () => { ignore = true; };
    // circleId too: these records are now scoped to it, and moving from
    // one circle's page to another does not remount this.
  }, [round, circleId]);

  // Its own effect, not folded into the one above: a team's league row is
  // a second round trip and the roster must not wait on it. A 404 here is
  // an ANSWER -- the circle is not matched to a side yet -- so it resolves
  // to null rather than throwing the screen away.
  useEffect(() => {
    let ignore = false;
    setTeamInLeague(undefined);
    myCircles().then((mine) => {
      const c = (mine.circles || []).find((x) => x.id === circleId);
      const league = ((c && c.leagues) || [])[0];
      if (!league) { if (!ignore) setTeamInLeague(null); return undefined; }
      if (!ignore) setTeamLeagueId(league.league_id);
      return get(`/api/leagues/${encodeURIComponent(league.league_id)}`
                 + `/teams/${encodeURIComponent(circleId)}`)
        .then((t) => { if (!ignore) setTeamInLeague(t); })
        .catch(() => { if (!ignore) setTeamInLeague(null); });
    }).catch(() => { if (!ignore) setTeamInLeague(null); });
    return () => { ignore = true; };
  }, [circleId, round]);

  if (error) {
    return html`
      <div class="screen">
        <button class="secondary" onClick=${onDone}>Back</button>
        <p class="error" data-testid="circle-error">${error}</p>
      </div>`;
  }
  if (rows === null) return html`<p class="sub">Loading…</p>`;

  const circle = circles.find((c) => c.id === circleId) || {};
  const isTeam = Boolean(circle.is_team);
  const mine = rows.find((r) => r.player_id === authState.user_id);
  const place = rows.findIndex((r) => r.player_id === authState.user_id) + 1;
  const ranked = new Set(rows.map((r) => r.player_id));
  const addable = accounts.filter((a) => !ranked.has(a.user_id));
  // Who on this roster is a GENERATED placeholder, from the circle itself
  // rather than from `rows`. `rows` has two sources — a team's own members,
  // and a leaderboard for everything else — and only one of them was ever
  // going to carry this, which is exactly the bug the first cut had: the
  // control appeared for teams and silently never for anything else.
  const claimable = new Set((circle.people || [])
    .filter((p) => p.claimable).map((p) => p.user_id));
  const formOf = (pid) => (h2h.find((r) => r.opponent_id === pid) || {});

  // Removing somebody, and leaving yourself, are the same call — the server
  // already knows the difference: your own is always allowed, somebody else's
  // needs authority over the circle.
  const remove = async (userId) => {
    try {
      await del(`/api/circles/${encodeURIComponent(circleId)}`
                + `/members/${encodeURIComponent(userId)}`);
      if (userId === authState.user_id) { onDone(); return; }
      setRound((n) => n + 1);
    } catch (e) { setError(e.detail || e.message); }
  };

  // A placeholder's archive identity, handed to a real account. Deliberately
  // NOT `setError`: that replaces the whole screen with the message, and both
  // of this call's refusals are ordinary answers to a reasonable press — the
  // placeholder has played a Cueforge match, or the account already holds a
  // different archive identity. Those belong beside the control, with the
  // roster still on screen, in the server's own words rather than reworded.
  const claim = async (ghostId, userId) => {
    if (!userId) return;
    setClaimError('');
    try {
      await post(`/api/circles/${encodeURIComponent(circleId)}/claim`,
                 { ghost_id: ghostId, user_id: userId });
      setRound((n) => n + 1);
    } catch (e) { setClaimError(e.detail || e.message); }
  };

  const header = html`
    <div class="rowbetween">
      <div>
        ${/* Named by what it is, wherever it is one -- a team is a circle
              entered into a league, and this says so rather than leaving it
              to be guessed from what has gone missing below. */''}
        <div class="sub" data-testid="circle-kind">
          ${isTeam ? 'Team' : 'Circle'}
        </div>
        <h2 data-testid="circle-name">${circle.name}</h2>
      </div>
      <button class="secondary" style="width:auto"
              data-testid="circle-back"
              onClick=${onDone}>Back</button>
    </div>`;

  const roster = html`
    <div class="stack">
      <label>Roster</label>
      ${rows.map((r) => {
        const record = formOf(r.player_id);
        const you = r.player_id === authState.user_id;
        return html`
          <div class="card rowbetween" key=${r.player_id} data-testid="roster-row">
            <div style="display:flex;align-items:center;gap:.6rem">
              ${/* A face where the crown ball is, so the row's height and
                    its gap are unchanged -- `.avatar` is sized to the same
                    1.75rem. Almost nobody has one, and `Picture` draws
                    nothing then, so the ball stays. Issue #66. */''}
              ${r.avatar
                ? html`<${Picture} imageId=${r.avatar} className="avatar"
                                   alt=${r.name} />`
                : html`
                  <div class="ball" style="width:1.75rem;height:1.75rem;
                                           display:flex;align-items:center;
                                           justify-content:center;color:#0A0C0F">
                    ${you && html`<${Icon} name="crown" size=${12} />`}
                  </div>`}
              <div>
                ${/* Issue #59, reopened: this roster was missed. There are
                      TWO lists over the same `rows` in this file -- this one
                      on Leaders, and the members list under Manage -- and
                      only the second had the mark. Two renders of one thing
                      is how half a feature ships. */''}
                <div data-testid="roster-name">
                  <${RoleMarks} roles=${r.roles} only=${1} />${r.name}
                </div>
                ${record.played > 0 && html`
                  <div class="sub mono" data-testid="roster-record">
                    ${`${record.won}–${record.lost} v you`}
                  </div>`}
              </div>
            </div>
            <span class="mono brassc" data-testid="roster-elo">
              ${Math.round(r.elo)}
            </span>
          </div>`;
      })}
    </div>`;

  // The bar stays; only what is under it changes. A circle opens on its
  // standings rather than on a menu, because a menu is not an answer to
  // anything — you came here to see how it is going. A team has no
  // standings to open on -- Leaders is the in-circle leaderboard, and a
  // team's is refused at the API, not merely hidden here -- so it opens on
  // Chat instead. Leaders itself is not offered to a team at all -- but the
  // room held a Match history button as well as the leaderboard, and that
  // part was never asked to go: a team's friendlies record from before it
  // became one is a real record of games really played, so it keeps a room
  // of its own, in Leaders' old slot in the bar, holding nothing but that.
  const defaultView = isTeam ? 'chat' : 'board';
  const wayList = isTeam
    ? [['matches', 'rotate-ccw', 'Matches'], ['chat', 'users', 'Chat'],
       ['manage', 'settings', 'Manage']]
    : [['board', 'bar-chart-3', 'Leaders'], ['chat', 'users', 'Chat'],
       ['manage', 'settings', 'Manage']];
  const effectiveView = view || defaultView;
  const ways = html`
    <nav class="ways" data-testid="circle-ways">
      ${wayList.map(([id, icon, label]) => html`
        <button key=${id} data-testid=${`circle-go-${id}`}
                aria-current=${effectiveView === id ? 'page' : undefined}
                onClick=${() => onGo('circle', circleId,
                                     id === defaultView ? null : id)}>
          <${Icon} name=${icon} size=${18} />
          <span>${label}</span>
        </button>`)}
    </nav>`;

  const summary = html`
    <div class="card" data-testid="circle-card"
         style="border-color:rgb(201 162 39 / .27);
                background:linear-gradient(180deg,#171207 0%,var(--raised) 100%)">
      <div class="rowbetween" style="align-items:flex-start">
        <div>
          <div class="sub">IN THIS CIRCLE</div>
          ${/* A count with a word in it, so the mono rather than the display
                face — Wallpoet is for a number read across a room, and
                "players" set in it is an ornament nobody asked for. */''}
          <div class="mono brassc" data-testid="circle-players"
               style="font-size:1.05rem;font-weight:700">
            ${`${rows.length} ${rows.length === 1 ? 'player' : 'players'}`}
          </div>
        </div>
        ${/* "Your place" is standing in the friendlies leaderboard -- a
              team does not have one, and this is the same fact the Leaders
              tab is missing for, so it goes with it rather than showing a
              dash that looks like an answer. */''}
        ${!isTeam && html`
          <div style="text-align:right">
            <div class="sub">YOUR PLACE</div>
            <div class="figure chalk" data-testid="circle-place"
                 style="font-size:1.15rem">
              ${place ? `${place}/${rows.length}` : '—'}
            </div>
          </div>`}
      </div>
    </div>`;

  const manage = html`
    <div class="stack" data-testid="circle-manage">
      <label>Add to this circle</label>
      <${AddToCircle} circleId=${circleId} addable=${addable}
                      onAdded=${() => setRound((n) => n + 1)} prefix="board" />
    </div>

    ${/* A crest for the team. Behind `may_roster`, the same authority that
          decides who is on it -- a badge is how the side presents itself and
          that is the same kind of decision. Issue #66. */''}
    ${circle.may_roster && html`
      <${ImageUpload} kind="circle" subjectId=${circleId}
                      label="A badge for this circle" />`}

    ${/* A team's own code. Whoever keeps this roster hands their players a
          code that puts them in the league AND in this team — the ordinary
          way in, and it should not need a league administrator to mint. */''}
    ${circle.may_roster && (circle.leagues || []).map((l) => html`
      <${TeamCode} key=${l.league_id} league=${l} circleId=${circleId} />`)}

    ${/* The team's own way out of a league. `withdraw_league_entry` allowed
          the committee and the venue; a side that folds mid-season is not
          waiting for the pub's landlord to notice, so whoever RUNS the team
          may do it too -- and deliberately not whoever merely picks the
          sheet. Offered on `may_run`, which is that same wildcard, rather
          than on `may_roster`, which the sheet-picker also passes. */''}
    ${circle.may_run && (circle.leagues || []).map((l) => html`
      <div class="card rowbetween" key=${`out-${l.league_id}`}
           data-testid=${`circle-withdraw-${l.league_id}`}>
        <span class="sub">${`Playing in ${l.name}`}</span>
        <button class="secondary" style="width:auto"
                data-testid=${`circle-withdraw-go-${l.league_id}`}
                onClick=${async () => {
                  setClaimError('');
                  try {
                    await del(`/api/leagues/${encodeURIComponent(l.league_id)}`
                              + `/entries/circle/${encodeURIComponent(circleId)}`);
                    setRound((n) => n + 1);
                  } catch (e) { setClaimError(e.detail || e.message); }
                }}>Withdraw from it</button>
      </div>`)}

    <div class="stack" data-testid="circle-members">
      <label>Who is in it</label>
      ${rows.map((r) => {
        const head = html`
          ${/* THE SECOND list over these same rows. circle.js draws them on
                Leaders as well, and issue #59 was reopened because a mark
                went on one and not the other -- so this one gets the face
                too. */''}
          <span style="display:flex;align-items:center;gap:.5rem">
            <${Picture} imageId=${r.avatar} className="avatar" alt=${r.name} />
            <${RoleMarks} roles=${r.roles} only=${1} />${r.name}
          </span>
          ${r.player_id !== authState.user_id && html`
            <button class="secondary" style="width:auto"
                    data-testid=${`member-remove-${r.player_id}`}
                    onClick=${() => remove(r.player_id)}>Remove</button>`}`;
        // An ordinary member keeps exactly the row it had. Only a
        // placeholder grows a second line, so the roster does not gain a
        // control on every row to serve the few that need one.
        if (!claimable.has(r.player_id)) {
          return html`
            <div class="card rowbetween" key=${r.player_id}
                 data-testid=${`member-${r.player_id}`}>${head}</div>`;
        }
        return html`
          <div class="card stack" key=${r.player_id}
               data-testid=${`member-${r.player_id}`}>
            <div class="rowbetween">${head}</div>
            ${/* Claiming YOURSELF is always allowed — the server says so, so
                  the option is offered to everybody. Handing a placeholder to
                  somebody ELSE is roster authority, which is why the rest of
                  the list is behind `may_roster`: offering names that would
                  be refused on press is a menu of disappointments. */''}
            <select class="combo" value=""
                    data-testid=${`member-claim-${r.player_id}`}
                    onChange=${(e) => claim(r.player_id, e.target.value)}>
              <option value="">Standing in — hand over to…</option>
              <option value=${authState.user_id}>This is me</option>
              ${circle.may_roster && accounts
                .filter((a) => a.user_id !== authState.user_id)
                .map((a) => html`
                  <option key=${a.user_id} value=${a.user_id}>
                    ${a.name}
                  </option>`)}
            </select>
          </div>`;
      })}
      ${claimError && html`
        <p class="error" data-testid="member-claim-error">${claimError}</p>`}
      ${/* Leaving your own circle is a different act from removing somebody
            else, and it is offered as one rather than hidden in a list. */''}
      <button class="secondary" data-testid="circle-leave"
              onClick=${() => remove(authState.user_id)}>Leave this circle</button>
    </div>

    ${/* Where it plays. A circle could be adopted by a pub and never let go
          of again -- `release_circle` has existed all along and nothing on
          any screen called it, so a venue could not be deleted while any
          circle still pointed at it, and there was no way to stop pointing
          (GitHub issue #22). */''}
    ${circle.may_roster && html`
      <div class="stack" data-testid="circle-venue">
        <label>Where it plays</label>
        ${circle.venue_id
          ? html`
            <div class="card rowbetween">
              <span data-testid="circle-venue-name">${circle.venue_name}</span>
              <button class="secondary" style="width:auto"
                      data-testid="circle-venue-leave"
                      onClick=${async () => {
                        setClaimError('');
                        try {
                          await del(`/api/venues/${encodeURIComponent(circle.venue_id)}`
                                    + `/circles/${encodeURIComponent(circleId)}`);
                          setRound((n) => n + 1);
                        } catch (e) { setClaimError(e.detail || e.message); }
                      }}>Leave this pub</button>
            </div>`
          : html`<p class="empty" data-testid="circle-venue-none">
                   Not at a pub.
                 </p>`}

        ${/* ASK, not move. The pub has to agree -- `request_adoption`'s own
              docstring says a venue helping itself to somebody's circle
              would be "a pub claiming a team that never chose it", and the
              same holds in reverse. So this is honest about what it does
              rather than calling it a move and leaving somebody wondering
              why nothing happened. */''}
        <label>Ask another pub to take it</label>
        <select class="combo" value="" data-testid="circle-venue-ask"
                onChange=${async (e) => {
                  const to = e.target.value;
                  if (!to) return;
                  setClaimError('');
                  try {
                    if (circle.venue_id) {
                      await del(`/api/venues/${encodeURIComponent(circle.venue_id)}`
                                + `/circles/${encodeURIComponent(circleId)}`);
                    }
                    await post(`/api/venues/${encodeURIComponent(to)}`
                               + `/circles/${encodeURIComponent(circleId)}/ask`, {});
                    setRound((n) => n + 1);
                  } catch (err) { setClaimError(err.detail || err.message); }
                }}>
          <option value="">Choose a pub…</option>
          ${venues.filter((v) => v.venue_id !== circle.venue_id).map((v) => html`
            <option key=${v.venue_id} value=${v.venue_id}>${v.name}</option>`)}
        </select>
        <p class="sub">
          It plays there once the pub accepts. Leaving is immediate.
        </p>
      </div>`}

    ${/* ISSUE #56: this circle's OWN members, not every account on the
          instance. It was `/api/people` — 472 accounts on production — so
          handing somebody a role here meant finding one person among every
          person who has ever signed up, most of whom have never played in
          this circle and cannot be given anything useful in it.

          `circle.people` is the roster the server already sends with each
          circle, and it leaves YOU out, which is right: you hold the grant
          that renders this screen. */''}
    <${CircleManagers} circleId=${circleId} listed=${circle.listed}
                       holds=${circle.holds}
                       isTeam=${isTeam} accounts=${circle.people || []}
                       page=${managePage} setPage=${setManagePage}
                       onChanged=${async () => setRound((r) => r + 1)}
                       onDeleted=${onDone} />`;

  const chat = html`<${Chat} base=${`/api/circles/${encodeURIComponent(circleId)}`}
                            me=${authState.user_id} />`;

  const leaders = html`
    ${roster}
    <button class="secondary" data-testid="circle-h2h"
            onClick=${() => onGo('h2h', circleId)}>Head to head in full</button>
    <button class="secondary" data-testid="circle-matches"
            onClick=${() => onGo('matches', circleId)}>Match history</button>`;

  // A team's replacement for Leaders: no roster (still reachable through
  // Manage) and no head-to-head button -- just the one thing that was never
  // asked to go with the other two.
  // A team's own first page. It used to offer ONE button, to a history of
  // friendlies played before the circle became a team -- the single set of
  // results a team is not about (GitHub issue #19). What belongs here is
  // what the team IS in its league: the roster with its stats, the ratings
  // and the fixtures, rendered from `TeamInLeague` so this and the league's
  // own team page cannot drift apart.
  //
  // The friendlies are not thrown away, just put where they belong -- under
  // the thing people came for, labelled as what they are.
  const teamMatches = html`
    <div class="stack" data-testid="circle-team-matches">
      ${teamInLeague === undefined
        ? html`<p class="sub">Loading…</p>`
        : teamInLeague === null
          ? html`
            <p class="sub" data-testid="circle-team-unlinked">
              This team is not matched to a side in its league yet, so there
              are no league stats to show.
            </p>`
          : html`<${TeamInLeague} team=${teamInLeague}
                                  leagueId=${teamLeagueId} onGo=${onGo} />`}

      <div class="stack">
        <label>Before it was a team</label>
        <p class="sub">
          Friendlies played in this circle before it entered a league.
        </p>
        <button class="secondary" data-testid="circle-matches"
                onClick=${() => onGo('matches', circleId)}>Match history</button>
      </div>
    </div>`;

  // A team offers no Leaders room at all (see `wayList` above) -- Matches
  // takes its slot instead. A stale `board` view -- reached only by typing
  // the URL, never by tapping a button that no longer exists -- falls back
  // to Chat rather than drawing the friendlies leaderboard it just refused
  // to fetch.
  const content = effectiveView === 'manage' ? manage
                : effectiveView === 'chat' ? chat
                : isTeam && effectiveView === 'matches' ? teamMatches
                : isTeam ? chat
                : leaders;

  return html`
    ${header}
    ${summary}
    ${ways}
    ${content}`;
}

/** One way in — an icon over a word, like the tab bar it is modelled on. */
function Way({ icon, label, testid, onClick }) {
  return html`
    <button data-testid=${testid} onClick=${onClick}>
      <${Icon} name=${icon} size=${18} />
      <span>${label}</span>
    </button>`;
}


/**
 * The code a team hands its own players.
 *
 * Bound to this circle, so redeeming it joins the league and the team in one
 * go. Issued by whoever keeps the roster — going through a league
 * administrator for every new player is the thing this avoids.
 */
function TeamCode({ league, circleId }) {
  const [code, setCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState('');

  const make = async () => {
    setBusy(true);
    setFailed('');
    try {
      const r = await post(
        `/api/leagues/${encodeURIComponent(league.league_id)}/invites`,
        { circle_id: circleId });
      setCode(r.code);
    } catch (e) {
      setFailed(e.detail || e.message);
    } finally { setBusy(false); }
  };

  return html`
    <div class="card stack" data-testid=${`team-code-${league.league_id}`}>
      <label>${`Code for ${league.name}`}</label>
      <p class="sub" style="margin:0">
        Puts them in the league and in this team.
      </p>
      <button class="secondary" disabled=${busy}
              data-testid="team-code-make" onClick=${make}>
        ${busy ? 'Making…' : 'Make a code'}
      </button>
      ${code && html`
        <div class="card" data-testid="team-code-shown">
          <p class="sub" style="margin:0 0 .4rem">
            Shown once. It lasts 90 days and does not run out when the first
            person uses it.
          </p>
          <code>${code}</code>
        </div>`}
      ${failed && html`<p class="error" data-testid="team-code-error">
                         ${failed}
                       </p>`}
    </div>`;
}
